import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { BaseViewComponent, Environment, Errors, RecaptchaMode } from '@sb/library/base';
import { SBDateTime } from '@sb/library/localization';
import { InvisibleReCaptchaComponent } from 'ngx-captcha';
import { interval, Subscription } from 'rxjs';

enum RecaptchaState {
    Invisible,
    WaitingToAppear,
    Visible
}

@Component({
    selector: 'lib-recaptcha',
    templateUrl: './recaptcha.component.html',
    styleUrl: './recaptcha.component.scss'
})
export class RecaptchaComponent extends BaseViewComponent {

    readonly connectionErrors: Errors = new Errors();
    readonly requiredErrors: Errors = new Errors();

    @ViewChild(InvisibleReCaptchaComponent) recaptchaComponent!: InvisibleReCaptchaComponent;

    @Input({required: true}) mode!: RecaptchaMode;
    @Input() isWidgetVisible: boolean = true;

    @Output() handleCaptcha: EventEmitter<void> = new EventEmitter();
    @Output() handleCaptchaError: EventEmitter<void> = new EventEmitter();

    private _resolvedRecaptchaToken: string | null = null;
    private _dateRecaptchaToken: SBDateTime | null = null;

    private _iframeObserver?: Subscription;
    private _state: RecaptchaState = RecaptchaState.Invisible;

    constructor(
        private _Environment: Environment
    ) {
        super();
        this.connectionErrors.Override({
            api: _ => $localize`:@@CommonRecaptchaErrorMessage:Could not connect to the reCAPTCHA service. Please check your internet connection and reload.`
        });

        this.requiredErrors.Override({
            api: _ => $localize`:@@CommonRecaptchaVerificationSubmitButtonErrorMessage:An error occurred during reCAPTCHA verification. Please try again.`
        });
    }

    HandleCAPTCHA(captchaResponse: string | null) {
        this.HandleIFrameHidden();
        
        this._resolvedRecaptchaToken = captchaResponse;
        this._dateRecaptchaToken = SBDateTime.Now();

        this.handleCaptcha.emit();
    }

    HandleCAPTCHAError(error: any) {
        this.HandleIFrameHidden();
        this.recaptchaComponent.resetCaptcha();

        this.handleCaptchaError.emit();
    }

    GetCaptcha(required: boolean): void {
        if (!required) {
            this.ResetCaptchaToken(false);
            this.handleCaptcha.emit();
        } else if (this._resolvedRecaptchaToken && this._dateRecaptchaToken!.Clone().AddMinutes(2).totalMilliseconds > SBDateTime.Now().totalMilliseconds) {
            this.handleCaptcha.emit();
        } else {
            this._state = RecaptchaState.WaitingToAppear;
            this._iframeObserver?.unsubscribe();
            this._iframeObserver = interval(100).subscribe(_ => {
                const captchaFrame = document.querySelector('div:has(div>iframe[title*="recaptcha"])') as HTMLElement;
                const isVisible = captchaFrame && captchaFrame.style.opacity !== '0';
                if (this._state === RecaptchaState.WaitingToAppear && isVisible) {
                    this._state = RecaptchaState.Visible;
                    const appRoot = document.querySelector('app-root') as HTMLElement;
                    const sizedIFrame = captchaFrame.querySelector('iframe') as HTMLElement;

                    // Make sure we can show the whole CAPTCHA
                    appRoot.style.minHeight = `${(parseFloat(sizedIFrame.style.height) + 20)}px`;
                    appRoot.style.display = 'block';
                } else if (this._state === RecaptchaState.Visible && !isVisible) {
                    this.HandleIFrameHidden();
                    this.handleCaptchaError.emit();
                }
            });

            setTimeout(() => {
                this.recaptchaComponent.execute();
            }, 1000);
        }
    }

    ResetCaptchaToken(resetWidget: boolean = true): void {
        this._resolvedRecaptchaToken = null;
        this._dateRecaptchaToken = null;
        if (resetWidget) {
            this.recaptchaComponent.resetCaptcha();
        }
    }

    private HandleIFrameHidden() {
        const appRoot = document.querySelector('app-root') as HTMLElement;
        appRoot.style.removeProperty('minHeight');
        appRoot.style.removeProperty('display');

        this._state = RecaptchaState.Invisible;
        this._iframeObserver!.unsubscribe();
    }

    get RecaptchaMode(): typeof RecaptchaMode {
        return RecaptchaMode;
    }

    get resolvedRecaptchaToken(): string | null {
        return this._resolvedRecaptchaToken;
    }

    get reCAPTCHASiteKey(): string {
        return this._Environment.Telemetry!.reCAPTCHA!.SiteKey!;
    }
}
